import { z } from 'zod'

import { lenientList, moneySchema } from '@/schemas/common'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * Reference data — `/api/v5/reference/`, in the app's own vocabulary.
 *
 * mm-be's `services/reference/` holds what NOBODY FILES: Nepal Rastra Bank's
 * regulatory limits, and the words a screen uses for a number once it has one
 * ("Normal", "Cheaper", "Within cap"). The same table reaches the web twice:
 * joined into every Advanced panel column (`reading`, `regulatory`), and on
 * its own route for the screens the panel does not cover — Technical's
 * momentum readings and Advanced · Trading's chips. Both arrive through the
 * schemas below, so one number can never be worded two ways on two tabs.
 *
 * VOCABULARY FIELDS ARE CLOSED HERE, NOT ON THE WIRE. mm-be serialises every
 * one of them as a bare CharField. The services narrow a value this build
 * does not know to `null` BEFORE these schemas run (`knownWord` in
 * `schemas/common.ts`), so an unrecognised word costs a chip, never a page.
 * `null` everywhere below means "nobody has decided", never "no" — mm-be's
 * own rule for this table.
 */

/**
 * Which direction of a metric is the good one. NEITHER is a decision too: the
 * metric has no good side (MACD against its signal line is a fact about
 * momentum, not a grade), so no BETTER/WORSE is ever printed for it.
 */
export const FAVOURABLE_DIRECTIONS = ['HIGHER', 'LOWER', 'NEITHER'] as const
export const favourableSchema = z.enum(FAVOURABLE_DIRECTIONS)

/** A sector comparison crossed with the metric's favourable direction. */
export const JUDGEMENTS = ['BETTER', 'WORSE', 'ABOUT_THE_SAME'] as const
export const judgementSchema = z.enum(JUDGEMENTS)

/** Which side of a regulatory limit is the legal side. */
export const LIMIT_COMPARATORS = ['MINIMUM', 'CEILING'] as const
export const limitComparatorSchema = z.enum(LIMIT_COMPARATORS)

/** A word that may not have been written yet. */
const wordSchema = z.string().min(1).nullable()

/**
 * One half-open band of a metric's own scale: `lower <= value < upper`, a null
 * bound unbounded on that side, a null `word` a band whose reading design has
 * not written. The list arrives in mm-be's display order and the FIRST band
 * that holds a value wins — see `bandWord`
 * (`features/reference/lib/bandWord.ts`), which is the only walk to use.
 */
export const thresholdBandSchema = z.object({
  lower: moneySchema.nullable(),
  upper: moneySchema.nullable(),
  word: wordSchema,
})

/**
 * How one metric is presented: its direction, its bands and its three
 * comparison words. For most metrics only ONE of the three words exists — the
 * board drew one case per metric and mm-be invents none — so a missing word
 * is no chip, not a reason to borrow another metric's.
 */
export const metricPresentationSchema = z.object({
  code: z.string().min(1),
  label: z.string().min(1),
  favourable: favourableSchema.nullable(),
  wordAbove: wordSchema,
  wordBelow: wordSchema,
  wordAboutTheSame: wordSchema,
  explainer: wordSchema,
  thresholds: z.array(thresholdBandSchema),
})

/**
 * The whole presentation table as rows. A row that does not parse is dropped
 * with a warning: one malformed metric costs that metric's words, not every
 * reading on the page.
 */
export const metricPresentationListSchema = lenientList(
  metricPresentationSchema,
  'metric presentation',
)

/**
 * What the presentation table says about ONE number, as the Advanced panel
 * joins it onto a column. Every field is independently nullable: a metric can
 * have a band but no direction, or a direction but no sector to apply it to.
 *
 *  - `reading`     the band word for the value on its own scale ("Normal")
 *  - `comparison`  the word for how it sits against its reference ("Cheaper")
 *  - `judgement`   BETTER / WORSE / ABOUT_THE_SAME — colour a verdict by THIS,
 *                  never by `sector.verdict` alone, which is only a direction
 */
export const metricReadingSchema = z.object({
  code: z.string().min(1),
  favourable: favourableSchema.nullable(),
  reading: wordSchema,
  comparison: wordSchema,
  judgement: judgementSchema.nullable(),
  explainer: wordSchema,
})

/**
 * One NRB reference line — regulation, not a filing. Several `code`s can share
 * one `rule` (the data service keys credit-to-deposit two ways), so anything
 * listing "the limits in force" groups on `rule`, or it draws one rule twice.
 */
export const regulatoryReferenceSchema = z.object({
  code: z.string().min(1),
  rule: z.string().min(1),
  /** "NRB minimum", "NRB ceiling", "Full cover", "NRB cap". */
  label: z.string().min(1),
  value: moneySchema,
  unit: z.string().min(1).nullable(),
  comparator: limitComparatorSchema.nullable(),
  /** Null: the limit applies to every sector no sector-specific row overrides. */
  sector: z.string().min(1).nullable(),
  wordWithin: wordSchema,
  /** Null on every limit mm-be ships: the board only ever drew the passing case. */
  wordBreached: wordSchema,
  explainer: wordSchema,
  source: wordSchema,
  effectiveFrom: isoDateSchema.nullable(),
})

/**
 * One figure measured against its limit.
 *
 * `within` is null when the figure was not reported — an unfiled capital
 * adequacy is unknown, not a breach. `headroom` is signed toward the LEGAL
 * side for both comparators (positive = room to spare, negative = a breach),
 * so "1.90 points of room" under a minimum and "10 points of room" under a
 * ceiling are one client rule.
 */
export const limitTestSchema = z.object({
  limit: regulatoryReferenceSchema,
  within: z.boolean().nullable(),
  headroom: moneySchema.nullable(),
  word: wordSchema,
})

export type Favourable = z.infer<typeof favourableSchema>
export type Judgement = z.infer<typeof judgementSchema>
export type LimitComparator = z.infer<typeof limitComparatorSchema>
export type ThresholdBand = z.infer<typeof thresholdBandSchema>
export type MetricPresentation = z.infer<typeof metricPresentationSchema>
/** The presentation table keyed by metric code (`rsi`, `adx`, …) — what the hook serves. */
export type MetricPresentations = Readonly<Record<string, MetricPresentation>>
export type MetricReading = z.infer<typeof metricReadingSchema>
export type RegulatoryReference = z.infer<typeof regulatoryReferenceSchema>
export type LimitTest = z.infer<typeof limitTestSchema>
